import { EVINCED_SEVERITIES, EVINCED_SEVERITIES_IDS } from '@evinced-private/ui-common';

import { IssueSeverities } from '../interfaces/IssuesOverviewResponse';

export type CustomSeverityNames = Record<string, string>;

/**
 * Maps each default severity ID (the key used in customSeverityNames) to its default display name.
 * The iteration order also defines the tier hierarchy used for grouping/dedup precedence.
 */
const DEFAULT_SEVERITY_BY_ID: Record<EVINCED_SEVERITIES_IDS, EVINCED_SEVERITIES> = {
	[EVINCED_SEVERITIES_IDS.CRITICAL]: EVINCED_SEVERITIES.CRITICAL,
	[EVINCED_SEVERITIES_IDS.SERIOUS]: EVINCED_SEVERITIES.SERIOUS,
	[EVINCED_SEVERITIES_IDS.MODERATE]: EVINCED_SEVERITIES.MODERATE,
	[EVINCED_SEVERITIES_IDS.MINOR]: EVINCED_SEVERITIES.MINOR,
	[EVINCED_SEVERITIES_IDS.NEEDS_REVIEW]: EVINCED_SEVERITIES.NEEDS_REVIEW,
	[EVINCED_SEVERITIES_IDS.BEST_PRACTICE]: EVINCED_SEVERITIES.BEST_PRACTICE
};

/**
 * The API's fixed UUID for each default severity (sent as `newId`). It stays the same when a tenant
 * renames the severity, so it resolves the default even when several ids share one custom name.
 */
const DEFAULT_SEVERITY_BY_UUID: Record<string, EVINCED_SEVERITIES> = {
	'3c68eb41-9227-481d-8eec-9dc8956b19c4': EVINCED_SEVERITIES.CRITICAL,
	'5bd03118-5883-4f43-8831-1544af3afe7c': EVINCED_SEVERITIES.SERIOUS,
	'b3916735-5371-4112-994e-f2fd909a42e1': EVINCED_SEVERITIES.MODERATE,
	'4637208d-4f70-483d-b589-ff591c7130a0': EVINCED_SEVERITIES.MINOR,
	'3c68eb41-9227-481d-8eec-9dc8956b1900': EVINCED_SEVERITIES.NEEDS_REVIEW,
	'778e3252-7359-4bd9-92d6-976d77e1dfd6': EVINCED_SEVERITIES.BEST_PRACTICE
};

const getDefaultSeverityId = (
	defaultSeverity: EVINCED_SEVERITIES
): EVINCED_SEVERITIES_IDS | undefined => {
	const entry = (
		Object.entries(DEFAULT_SEVERITY_BY_ID) as [EVINCED_SEVERITIES_IDS, EVINCED_SEVERITIES][]
	).find(([, name]) => name === defaultSeverity);
	return entry?.[0];
};

/**
 * Resolves an issue's severity to its default severity name, for UI that only knows the six
 * defaults (e.g. the issue details severity badge colors). Returns undefined when it can't tell.
 *  1. `newId`, when it is a default id (case-insensitive — the API and mocks disagree on casing)
 *     or a default severity's UUID. Both stay the same when a tenant renames the severity.
 *  2. `name`, when it is already a default name.
 */
export const getDefaultSeverity = (
	severity: { name?: string; newId?: string } | undefined
): EVINCED_SEVERITIES | undefined =>
	DEFAULT_SEVERITY_BY_ID[severity?.newId?.toUpperCase() as EVINCED_SEVERITIES_IDS] ??
	DEFAULT_SEVERITY_BY_UUID[severity?.newId?.toLowerCase() ?? ''] ??
	Object.values(DEFAULT_SEVERITY_BY_ID).find((name) => name === severity?.name);

/**
 * Returns the custom display label for a default severity, falling back to the canonical
 * default name when no custom mapping is configured for it.
 */
export const resolveSeverityLabel = (
	defaultSeverity: EVINCED_SEVERITIES,
	customSeverityNames: CustomSeverityNames = {}
): string => {
	const id = getDefaultSeverityId(defaultSeverity);
	if (!id) {
		return defaultSeverity;
	}
	return customSeverityNames[id] ?? defaultSeverity;
};

/**
 * Resolves the label to use for a given default severity across an aggregated tenant view
 * (e.g. dashboard widgets that mix scans/issues from many properties). Returns the agreed
 * custom name only when every property in scope maps the severity to the same value, and
 * falls back to the canonical default whenever mappings are missing or diverge — this keeps
 * aggregated counts from being mislabelled when tenants partially renamed severities.
 */
export const resolveTenantWideSeverityLabel = (
	defaultSeverity: EVINCED_SEVERITIES,
	customSeverityNamesByPropertyId: Record<string, CustomSeverityNames> = {}
): string => {
	const labels = Object.values(customSeverityNamesByPropertyId)
		.map((mapping) => resolveSeverityLabel(defaultSeverity, mapping))
		.filter(Boolean);
	if (!labels.length) {
		return defaultSeverity;
	}
	return labels.every((label) => label === labels[0]) ? labels[0] : defaultSeverity;
};

/**
 * Returns the set of stored severity bucket names that should be aggregated when the UI asks
 * for a given default severity. Issues are persisted with the already-remapped severity name,
 * so all defaults that share a custom name collapse into the same stored bucket.
 */
export const getAggregatedSeverityBuckets = (
	defaultSeverity: EVINCED_SEVERITIES,
	customSeverityNames: CustomSeverityNames = {}
): Set<string> => {
	return new Set([resolveSeverityLabel(defaultSeverity, customSeverityNames)]);
};

/**
 * Sums the issue counts of every severity bucket that maps to the requested default severity.
 * When no custom mapping is provided this is equivalent to looking up the bucket by its default name.
 */
export const sumIssuesForDefaultSeverity = (
	severities: IssueSeverities[] | undefined,
	defaultSeverity: EVINCED_SEVERITIES,
	customSeverityNames: CustomSeverityNames = {}
): number => {
	if (!severities?.length) {
		return 0;
	}
	const bucketNames = getAggregatedSeverityBuckets(defaultSeverity, customSeverityNames);
	return severities
		.filter((s) => bucketNames.has(s.severity))
		.reduce((acc, s) => acc + (s.issuesCount || 0), 0);
};

type SeveritySummaryEntry = {
	label: string;
	value: number;
	defaultSeverities: EVINCED_SEVERITIES[];
};

/**
 * Builds the list of summary entries to render for a set of default severities, applying the
 * general dedup rule: when multiple defaults map to the same custom name, they collapse into a
 * single entry whose value is the shared bucket count and whose `defaultSeverities` list keeps
 * the original tier order. Entries are returned in the order of `defaultSeveritiesToShow`.
 */
export const buildDeduplicatedSeveritySummary = (
	severities: IssueSeverities[] | undefined,
	defaultSeveritiesToShow: EVINCED_SEVERITIES[],
	customSeverityNames: CustomSeverityNames = {}
): SeveritySummaryEntry[] => {
	const byLabel = new Map<string, SeveritySummaryEntry>();
	defaultSeveritiesToShow.forEach((defaultSeverity) => {
		const label = resolveSeverityLabel(defaultSeverity, customSeverityNames);
		const existing = byLabel.get(label);
		if (existing) {
			existing.defaultSeverities.push(defaultSeverity);
			return;
		}
		byLabel.set(label, {
			label,
			value: sumIssuesForDefaultSeverity(severities, defaultSeverity, customSeverityNames),
			defaultSeverities: [defaultSeverity]
		});
	});
	return Array.from(byLabel.values());
};

/**
 * Same dedup contract as `buildDeduplicatedSeveritySummary`, but built from already-bucketed
 * per-default counts (e.g. drilldown summaries that expose `critical`/`serious` typed fields
 * instead of a `severities` array). Counts for defaults that resolve to the same custom name
 * are summed into a single entry.
 */
export const buildDeduplicatedSeveritySummaryFromCounts = (
	countsByDefaultSeverity: Partial<Record<EVINCED_SEVERITIES, number>>,
	defaultSeveritiesToShow: EVINCED_SEVERITIES[],
	customSeverityNames: CustomSeverityNames = {}
): SeveritySummaryEntry[] => {
	const byLabel = new Map<string, SeveritySummaryEntry>();
	defaultSeveritiesToShow.forEach((defaultSeverity) => {
		const label = resolveSeverityLabel(defaultSeverity, customSeverityNames);
		const value = countsByDefaultSeverity[defaultSeverity] ?? 0;
		const existing = byLabel.get(label);
		if (existing) {
			existing.value += value;
			existing.defaultSeverities.push(defaultSeverity);
			return;
		}
		byLabel.set(label, {
			label,
			value,
			defaultSeverities: [defaultSeverity]
		});
	});
	return Array.from(byLabel.values());
};
